/**
 * sceneWomensFashion — a luxury lookbook for "MAISON NÔTRE".
 *
 * Five sections, one viewport wherever possible:
 *   1. HERO — full-bleed atmospheric backdrop, eyebrow + name + subhead
 *   2. EDITORIAL — single full-bleed look image with caption overlay
 *   3. LOOKBOOK — 4 product cards in a row (real dress photos from
 *      cupshe-blue-striped-dress/01, 04, 07, 10), glass info panels,
 *      per-card filter chain, prices in EUR
 *   4. MANIFESTO — full-width editorial pull quote
 *   5. FOOTER — minimal credits
 *
 * All animations driven by update callbacks — pixel-perfect via the
 * scene-graph API, no compileHtml, no DOM dependencies inside the scene.
 */

import {
  type Scene,
  type SceneNode,
  createScene,
  makeRect,
  makeText,
  makeImage,
  addNode,
} from "../sceneGraph";

const PALETTE = {
  cream: { r: 0.96, g: 0.92, b: 0.86 },
  cream2: { r: 0.92, g: 0.88, b: 0.82 },
  ivory: { r: 0.97, g: 0.95, b: 0.92 },
  chocolate: { r: 0.10, g: 0.06, b: 0.04 },
  gold: { r: 0.76, g: 0.60, b: 0.40 },
  gold2: { r: 0.88, g: 0.78, b: 0.62 },
  terracotta: { r: 0.62, g: 0.42, b: 0.32 },
  rose: { r: 0.78, g: 0.50, b: 0.45 },
  black: { r: 0.04, g: 0.03, b: 0.02 },
};

export function sceneWomensFashion(w: number, h: number): Scene {
  const scene = createScene();
  let z = 0;
  const T = () => performance.now();
  const isPhone = w < 600;
  const pad = isPhone ? 20 : 56;

  // ============================================================
  // 1. PAGE BACKDROP — atmospheric gradient + drifting orbs
  // ============================================================
  addNode(scene, makeRect({
    x: 0, y: 0, w, h,
    fill: { r: PALETTE.black.r, g: PALETTE.black.g, b: PALETTE.black.b, a: 1 },
    gradientEnd: { r: 0.18, g: 0.10, b: 0.08, a: 1 },
    gradientAngle: Math.PI * 0.6,
  })); z++;

  // Slow warm orb — top-right
  const orb1 = makeRect({
    x: w * 0.7, y: -120, w: 540, h: 540,
    radius: 270,
    fill: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 0.16 },
    filter: { blur: 0, brightness: 1.0, contrast: 1, saturate: 1.2, hueRotate: 0, invert: 0 },
  });
  addNode(scene, orb1); z++;
  orb1.update = (n) => {
    n.x = w * 0.7 + Math.sin(T() / 4500) * 28;
    n.y = -120 + Math.cos(T() / 5200) * 18;
  };

  // Terracotta orb — bottom-left
  const orb2 = makeRect({
    x: -180, y: h * 0.55, w: 420, h: 420,
    radius: 210,
    fill: { r: PALETTE.terracotta.r, g: PALETTE.terracotta.g, b: PALETTE.terracotta.b, a: 0.14 },
    filter: { blur: 0, brightness: 1.0, contrast: 1, saturate: 1.3, hueRotate: 0, invert: 0 },
  });
  addNode(scene, orb2); z++;
  orb2.update = (n) => {
    n.x = -180 + Math.cos(T() / 5400) * 22;
    n.y = h * 0.55 + Math.sin(T() / 4900) * 16;
  };

  // ============================================================
  // 2. NAV BAR — minimal
  // ============================================================
  // Brand mark (left)
  const navY = 28;
  addNode(scene, makeRect({
    x: pad, y: navY, w: 12, h: 12,
    radius: 1,
    fill: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 1 },
  })); z++;
  addNode(scene, makeText({
    x: pad + 18, y: navY - 2, w: 200, h: 18,
    text: "MAISON NÔTRE",
    fontSize: 12,
    fontWeight: 700,
    textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 0.95 },
    letterSpacing: 0.36,
    fontFamily: "system-ui, -apple-system, 'Times New Roman', serif",
  })); z++;

  // Right nav — desktop only
  if (!isPhone) {
    const items = ["COLLECTION", "ATELIER", "JOURNAL", "BOUTIQUES"];
    let cx = w - pad;
    for (let i = items.length - 1; i >= 0; i--) {
      const text = items[i]!;
      const tw = 90;
      cx -= tw;
      addNode(scene, makeText({
        x: cx, y: navY - 2, w: tw, h: 18,
        text,
        fontSize: 10,
        fontWeight: 500,
        textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 0.65 },
        textAlign: "center",
        letterSpacing: 0.22,
        fontFamily: "system-ui, -apple-system, sans-serif",
      })); z++;
    }
  }

  // ============================================================
  // 3. HERO ACT — editorial headline + CTA
  // ============================================================
  const heroY = isPhone ? 90 : 110;

  // Eyebrow — small caps
  addNode(scene, makeText({
    x: pad, y: heroY, w: 300, h: 14,
    text: "AUTOMNE / HIVER · MMXXVI",
    fontSize: 10,
    fontWeight: 600,
    textColor: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 0.9 },
    letterSpacing: 0.5,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // MASSIVE serif wordmark
  const wordmark = makeText({
    x: pad, y: heroY + 28,
    w: isPhone ? w - pad * 2 : w - pad * 2,
    h: 120,
    text: "MAISON NÔTRE",
    fontSize: isPhone ? 48 : 84,
    fontWeight: 800,
    textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 1 },
    letterSpacing: 0.04,
    lineHeight: 1.0,
    fontFamily: "'Times New Roman', Georgia, serif",
  });
  addNode(scene, wordmark); z++;
  // Letter-spacing breathes
  wordmark.update = (n) => {
    n.letterSpacing = 0.04 + Math.sin(T() / 3200) * 0.01;
  };

  // Italic subhead
  addNode(scene, makeText({
    x: pad, y: heroY + 140,
    w: isPhone ? w - pad * 2 : 640,
    h: 30,
    text: "— the articulation of absence",
    fontSize: isPhone ? 16 : 22,
    fontWeight: 400,
    textColor: { r: PALETTE.gold2.r, g: PALETTE.gold2.g, b: PALETTE.gold2.b, a: 0.95 },
    fontFamily: "Georgia, 'Times New Roman', serif",
  })); z++;

  // Thin gold rule
  addNode(scene, makeRect({
    x: pad, y: heroY + 184, w: 64, h: 1,
    fill: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 0.7 },
  })); z++;

  // CTA pill
  const ctaX = pad, ctaY = heroY + 200;
  const ctaW = 200, ctaH = 40;
  addNode(scene, makeRect({
    x: ctaX, y: ctaY, w: ctaW, h: ctaH,
    radius: 0,
    fill: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 1 },
  })); z++;
  addNode(scene, makeText({
    x: ctaX, y: ctaY, w: ctaW, h: ctaH,
    text: "DISCOVER THE COLLECTION",
    fontSize: 10,
    fontWeight: 700,
    textColor: { r: PALETTE.chocolate.r, g: PALETTE.chocolate.g, b: PALETTE.chocolate.b, a: 1 },
    textAlign: "center",
    letterSpacing: 0.24,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // ============================================================
  // 4. EDITORIAL — single full-bleed look image with caption
  // ============================================================
  // Position is offset to the right on desktop for editorial layout
  const lookY = isPhone ? 380 : 380;
  const lookH = isPhone ? 220 : 280;
  const lookX = isPhone ? 0 : w * 0.32;
  const lookW = isPhone ? w : w - lookX;

  // Hero photo (use view-02 as a featured look — the dress main shot)
  addNode(scene, makeImage({
    x: lookX, y: lookY, w: lookW, h: lookH,
    imageUrl: "/products/cupshe-blue-striped-dress/view-02.jpg",
  })); z++;

  // Caption overlay (right side on desktop)
  if (!isPhone) {
    addNode(scene, makeText({
      x: pad, y: lookY + 30, w: w * 0.28, h: 16,
      text: "LOOK · 02",
      fontSize: 10,
      fontWeight: 600,
      textColor: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 0.85 },
      letterSpacing: 0.4,
      fontFamily: "system-ui, -apple-system, sans-serif",
    })); z++;
    addNode(scene, makeText({
      x: pad, y: lookY + 56, w: w * 0.28, h: 64,
      text: "The Dawn Dress",
      fontSize: 30,
      fontWeight: 600,
      textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 1 },
      lineHeight: 1.0,
      fontFamily: "Georgia, 'Times New Roman', serif",
    })); z++;
    addNode(scene, makeText({
      x: pad, y: lookY + 130, w: w * 0.28, h: 40,
      text: "Hand-loomed silk crepe · 2026 collection",
      fontSize: 12,
      fontWeight: 400,
      textColor: { r: PALETTE.cream2.r, g: PALETTE.cream2.g, b: PALETTE.cream2.b, a: 0.75 },
      fontFamily: "Georgia, serif",
    })); z++;
  }

  // ============================================================
  // 5. LOOKBOOK — 4 product cards
  // ============================================================
  const lkY = lookY + lookH + 32;
  const lkH = isPhone ? 280 : 320;
  if (lkY + lkH < h - 80) {
    const lkGap = 14;
    const cardW = (w - pad * 2 - lkGap * 3) / 4;
    const cardH = lkH;

    const products = [
      { num: "N°01", name: "Le Drapé Crème", price: "€ 1,840",
        img: "/products/cupshe-blue-striped-dress/view-01.jpg" },
      { num: "N°02", name: "La Soie Terracotta", price: "€ 2,120",
        img: "/products/cupshe-blue-striped-dress/view-04.jpg" },
      { num: "N°03", name: "L'Argent Plissé", price: "€ 3,460",
        img: "/products/cupshe-blue-striped-dress/view-07.jpg" },
      { num: "N°04", name: "Le Velours Nuit", price: "€ 4,280",
        img: "/products/cupshe-blue-striped-dress/view-10.jpg" },
    ];

    for (let i = 0; i < products.length; i++) {
      const p = products[i]!;
      const cx = pad + i * (cardW + lkGap);
      const cy = lkY;
      const imgH = cardH * 0.7;

      // Card frame
      addNode(scene, makeRect({
        x: cx, y: cy, w: cardW, h: cardH,
        radius: 0,
        fill: { r: 0.06, g: 0.04, b: 0.03, a: 1 },
        borderWidths: [1, 1, 1, 1],
        borderColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 0.12 },
      })); z++;

      // Real product photo
      addNode(scene, makeImage({
        x: cx, y: cy, w: cardW, h: imgH,
        imageUrl: p.img,
      })); z++;

      // Image subtle overlay for cohesion
      addNode(scene, makeRect({
        x: cx, y: cy, w: cardW, h: imgH,
        fill: { r: PALETTE.chocolate.r, g: PALETTE.chocolate.g, b: PALETTE.chocolate.b, a: 0.08 },
      })); z++;

      // Card number top-right
      addNode(scene, makeText({
        x: cx + 10, y: cy + 8, w: cardW - 20, h: 14,
        text: p.num,
        fontSize: 10,
        fontWeight: 600,
        textColor: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 0.85 },
        textAlign: "right",
        letterSpacing: 0.18,
        fontFamily: "Georgia, serif",
      })); z++;

      // Info panel bottom
      const infoY = cy + imgH;
      const infoH = cardH - imgH;
      addNode(scene, makeRect({
        x: cx, y: infoY, w: cardW, h: infoH,
        fill: { r: 0.05, g: 0.03, b: 0.02, a: 1 },
      })); z++;

      addNode(scene, makeText({
        x: cx + 12, y: infoY + 10, w: cardW - 24, h: 16,
        text: p.name.toUpperCase(),
        fontSize: 10,
        fontWeight: 600,
        textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 0.95 },
        letterSpacing: 0.14,
        fontFamily: "Georgia, 'Times New Roman', serif",
      })); z++;

      addNode(scene, makeText({
        x: cx + 12, y: infoY + 30, w: cardW - 24, h: 14,
        text: p.price,
        fontSize: 11,
        fontWeight: 600,
        textColor: { r: PALETTE.gold.r, g: PALETTE.gold.g, b: PALETTE.gold.b, a: 0.95 },
        fontFamily: "system-ui, -apple-system, sans-serif",
      })); z++;

      addNode(scene, makeText({
        x: cx + 12, y: infoY + 50, w: cardW - 24, h: 10,
        text: "VIEW →",
        fontSize: 8,
        fontWeight: 700,
        textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 0.7 },
        letterSpacing: 0.3,
        fontFamily: "system-ui, -apple-system, sans-serif",
      })); z++;
    }
  }

  // ============================================================
  // 6. FOOTER — credits
  // ============================================================
  addNode(scene, makeText({
    x: pad, y: h - 30, w: w - pad * 2, h: 14,
    text: "MAISON NÔTRE · PARIS · MMXXVI",
    fontSize: 9,
    fontWeight: 600,
    textColor: { r: PALETTE.cream.r, g: PALETTE.cream.g, b: PALETTE.cream.b, a: 0.5 },
    textAlign: "center",
    letterSpacing: 0.4,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  return scene;
}
